import { flexRender, type Row } from '@tanstack/react-table';
import clsx from 'clsx';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import React from 'react';
import { Spinner } from 'react-bootstrap';
import { MdPlaylistRemove } from 'react-icons/md';
import { useDataTable } from '../DataTable';
import DataTableSelectionControls from './DataTableSelectionControls';

dayjs.locale('pt-br');

/** Um bloco de linhas; `header` vira uma linha de largura total antes delas e `collapsed` esconde as linhas. */
export type DataTableRowGroup = {
  key: string;
  header?: React.ReactNode;
  collapsed?: boolean;
  rows: Row<object>[];
};

type DataTableBodyProps = {
  showHeader?: boolean;
  /** Reparte as linhas da página em grupos (ex.: por fornecedor). Sem ela, uma lista só. */
  groupRows?: (rows: Row<object>[]) => DataTableRowGroup[];
  /** Classe extra de cada linha (ex.: subtotal em destaque). */
  rowClassName?: (row: Row<object>) => string | undefined;
};

type ColumnMeta = {
  className?: string;
  align?: 'left' | 'center' | 'right';
};

const alignClass = (meta?: ColumnMeta): string => {
  if (meta?.align === 'right') return 'text-end';
  if (meta?.align === 'center') return 'text-center';
  return '';
};

const getAriaSort = (sorted: false | 'asc' | 'desc'): 'none' | 'ascending' | 'descending' => {
  if (sorted === 'asc') return 'ascending';
  if (sorted === 'desc') return 'descending';
  return 'none';
};

const DataTableBody: React.FC<DataTableBodyProps> = ({ showHeader = true, groupRows, rowClassName }) => {
  const {
    table,
    placeHolder,
    isLoading,
    rowHeightMode,
    variant,
    showRowIndex,
    bulkActionComponents,
    selectedRowIds,
    renderSubComponent,
    onRowClick,
  } = useDataTable();

  if (!table) return null;

  const hasSelection = bulkActionComponents && bulkActionComponents.length > 0 && selectedRowIds.length > 0;
  const isEk = variant === 'list' || variant === 'report';
  const isCompact = rowHeightMode === 'compact';

  const tableClassName = clsx('table align-middle fs-6', {
    'table-row-dashed table-row-gray-300': variant === 'classic',
    'ek-table ek-table--list': variant === 'list',
    'ek-table ek-table--report': variant === 'report',
    'gy-1': variant === 'report' && isCompact,
    'gy-2': (variant === 'report' && !isCompact) || (variant !== 'report' && isCompact),
    'gy-3': variant !== 'report' && !isCompact,
  });

  const showIndexColumn = variant === 'report' && showRowIndex;
  const totalColSpan = table.getAllColumns().length + (showIndexColumn ? 1 : 0);

  // Row click navigates (ERPNext-style), but never when the click lands on an
  // interactive element — checkbox, link, button or the row-actions dropdown.
  const handleRowClick = (e: React.MouseEvent<HTMLTableRowElement>, row: Row<object>) => {
    if ((e.target as HTMLElement).closest('a, button, input, label, select, [role="button"], .dropdown, .form-check')) {
      return;
    }
    onRowClick?.(row);
  };

  const rows = table.getRowModel().rows;
  const groups: DataTableRowGroup[] = groupRows ? groupRows(rows) : [{ key: 'rows', rows }];

  const renderRow = (row: Row<object>): React.ReactElement => (
    <React.Fragment key={row.id}>
      <tr
        className={clsx(rowClassName?.(row), onRowClick && 'cursor-pointer ek-table__row--clickable') || undefined}
        onClick={onRowClick ? (e) => handleRowClick(e, row) : undefined}
      >
        {showIndexColumn ? <td className="ek-table__index align-middle">{row.index + 1}</td> : null}
        {row.getVisibleCells().map((cell) => {
          const meta: ColumnMeta | undefined = cell.column.columnDef.meta;
          const isSelectCell = cell.column.id === 'select';
          // O label encaminha o clique ao input, que resolve pelo onChange.
          // Aqui só alternamos cliques na moldura da célula.
          const onSelectCellClick = isSelectCell
            ? (e: React.MouseEvent<HTMLTableCellElement>) => {
                e.stopPropagation();
                if ((e.target as HTMLElement).closest('input, label')) return;
                cell.row.toggleSelected(!cell.row.getIsSelected());
              }
            : undefined;
          return (
            <td
              key={cell.id}
              className={clsx('align-middle', alignClass(meta), meta?.className, {
                'cursor-pointer': isSelectCell,
              })}
              onClick={onSelectCellClick}
            >
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </td>
          );
        })}
      </tr>
      {renderSubComponent && row.getIsExpanded() ? (
        <tr className="ek-table__detail-row">
          <td className="p-0" colSpan={totalColSpan}>
            <div className="ek-row-detail">{renderSubComponent(row)}</div>
          </td>
        </tr>
      ) : null}
    </React.Fragment>
  );

  const renderEmptyState = (): React.ReactElement => (
    <div className="d-flex flex-column h-100 align-items-center justify-content-center">
      {isLoading ? (
        <Spinner animation="border" variant="primary" />
      ) : placeHolder ? (
        placeHolder
      ) : (
        <>
          <MdPlaylistRemove className="text-gray-500" style={{ fontSize: '5rem' }} />
          <span className="text-gray-500 fw-bold mt-1 fs-7">Lista vazia…</span>
        </>
      )}
    </div>
  );

  return (
    <div className="table-responsive mt-5 position-relative">
      {!!isLoading && rows.length > 0 && (
        <div
          className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ backgroundColor: 'var(--bs-body-bg)', opacity: 0.7, zIndex: 1 }}
        >
          <Spinner animation="border" variant="primary" />
        </div>
      )}
      <table className={tableClassName}>
        {!!showHeader &&
          (hasSelection ? (
            <DataTableSelectionControls colSpan={totalColSpan} />
          ) : (
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className={isEk ? 'ek-table__hrow' : 'fw-bolder fs-6 text-gray-600'}>
                  {showIndexColumn ? <th className="ek-table__index">#</th> : null}
                  {headerGroup.headers.map((header) => {
                    const meta: ColumnMeta | undefined = header.column.columnDef.meta;
                    const canSort = header.column.getCanSort();
                    const sorted = header.column.getIsSorted();
                    return (
                      <th
                        key={header.id}
                        aria-sort={canSort ? getAriaSort(sorted) : undefined}
                        className={clsx(alignClass(meta), meta?.className)}
                      >
                        {header.isPlaceholder ? null : canSort ? (
                          <button
                            className="d-inline-flex align-items-center text-nowrap cursor-pointer select-none border-0 bg-transparent p-0"
                            style={{ color: 'inherit' }}
                            type="button"
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            <i
                              aria-hidden="true"
                              className={clsx(
                                'bi ms-1 ek-table__sort',
                                sorted === 'asc' && 'bi-arrow-up active',
                                sorted === 'desc' && 'bi-arrow-down active',
                                !sorted && 'bi-arrow-down-up',
                              )}
                            />
                          </button>
                        ) : (
                          <div className="d-inline-flex align-items-center text-nowrap">
                            {flexRender(header.column.columnDef.header, header.getContext())}
                          </div>
                        )}
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
          ))}
        {rows.length ? (
          groups.map((group) => (
            <tbody key={group.key}>
              {group.header ? (
                <tr>
                  <th className="bg-light" colSpan={totalColSpan} scope="rowgroup">
                    {group.header}
                  </th>
                </tr>
              ) : null}
              {group.collapsed ? null : group.rows.map(renderRow)}
            </tbody>
          ))
        ) : (
          <tbody>
            <tr>
              <td className="text-center" colSpan={totalColSpan}>
                {renderEmptyState()}
              </td>
            </tr>
          </tbody>
        )}
      </table>
    </div>
  );
};

export default DataTableBody;
